// Loyalty API layer.
//
// Follows the convention established in features/reports/api.ts and
// features/customers/api.ts:
//   - READs are wrapped in `withFallback` so missing backend endpoints fall
//     through to typed empty defaults (the UI renders an empty state).
//   - WRITEs (config updates) are NOT wrapped — a failed save must surface
//     as a toast error, not silent success.

import { apiClient, ApiError } from '@/lib/api-client';
import { customersApi } from '@/features/customers';
import { getCurrencySymbol, getTenantCurrency } from '@/hooks/use-tenant-currency';
import type { LoyaltyConfig, LoyaltyStats, LoyaltyTransactionRow, LoyaltyTxType, TopLoyaltyCustomerRow } from './types';

/**
 * Backend endpoints referenced by the Loyalty feature that are not yet wired
 * in services/api. The UI degrades gracefully when these return 404/501.
 */
export const BACKEND_GAPS = [
  'GET  /api/v1/loyalty/config',
  'PUT  /api/v1/loyalty/config',
  'GET  /api/v1/loyalty/stats',
  'GET  /api/v1/customers?sort=loyalty_points (ordering)',
] as const;

async function withFallback<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ApiError && (err.statusCode === 404 || err.statusCode === 501)) {
      return fallback;
    }
    throw err;
  }
}

// A function, not a constant: the earn-rate label names the tenant's currency.
const emptyConfig = (): LoyaltyConfig => ({
  points_per_rupee: 0.01, // 1 point per ₹100 — matches CRM default
  min_redemption_points: 100,
  redemption_value_paise: 50, // ₹0.50 per point
  expiry_months: 12,
  is_active: false,
  earn_rate_label: `1 point per ${getCurrencySymbol(getTenantCurrency())}100`,
});

const emptyStats: LoyaltyStats = {
  active_members: 0,
  total_points_issued: 0,
  total_points_redeemed: 0,
  redemption_rate_pct: 0,
  avg_points_per_member: 0,
  top_tier_member_count: 0,
};

export const loyaltyApi = {
  async getConfig(): Promise<LoyaltyConfig> {
    return withFallback(() => apiClient<LoyaltyConfig>('/loyalty/config'), emptyConfig());
  },

  /**
   * Saves program configuration. Intentionally NOT wrapped in withFallback:
   * if the backend is unavailable the user needs to know the save failed.
   */
  async updateConfig(input: LoyaltyConfig): Promise<LoyaltyConfig> {
    return apiClient<LoyaltyConfig>('/loyalty/config', { method: 'PUT', body: input });
  },

  async getStats(): Promise<LoyaltyStats> {
    return withFallback(() => apiClient<LoyaltyStats>('/loyalty/stats'), emptyStats);
  },

  /**
   * Top N loyalty customers. Reuses the existing customers list endpoint
   * with `sort=loyalty_points`. If the backend ignores the sort param the
   * hook below falls back to a client-side sort of whatever came back.
   */
  async getTopCustomers(limit = 10): Promise<TopLoyaltyCustomerRow[]> {
    const result = await customersApi.list({ sort: 'loyalty_points', limit, page: 1 });
    // Client-side guarantee — survives a backend that doesn't honour `sort`.
    return [...result.data]
      .sort((a, b) => (b.loyalty_points ?? 0) - (a.loyalty_points ?? 0))
      .slice(0, limit)
      .map((c) => ({
        id: c.id,
        name: c.name,
        phone: c.phone,
        loyalty_points: c.loyalty_points,
        loyalty_tier: c.loyalty_tier,
        last_visit_at: c.last_visit_at,
        total_spent: c.total_spent,
      }));
  },

  async listTransactions(params: { type?: LoyaltyTxType; limit?: number } = {}): Promise<LoyaltyTransactionRow[]> {
    const qs = new URLSearchParams();
    if (params.type) qs.set('type', params.type);
    if (params.limit) qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return withFallback(() => apiClient<LoyaltyTransactionRow[]>(`/loyalty/transactions${suffix}`), []);
  },
};
